import { useEffect, useMemo, useRef, useState } from 'react';
import { MagnifyingGlass } from '@phosphor-icons/react';
import { useStore } from '../state/store';
import { altitudeColor, formatAltitude } from '../map/altitude';
import type { Aircraft } from '../types';

/** Find an aircraft in view by callsign or ICAO24 hex, then select + fly to it. */
export default function SearchBox() {
  const aircraft = useStore((s) => s.aircraft);
  const select = useStore((s) => s.select);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const matches = useMemo(() => {
    const q = query.trim().toUpperCase();
    if (!q) return [];
    const out: Aircraft[] = [];
    for (const ac of aircraft.values()) {
      const cs = (ac.callsign ?? '').toUpperCase();
      if (cs.includes(q) || ac.icao24.toUpperCase().includes(q)) {
        out.push(ac);
        if (out.length >= 8) break;
      }
    }
    return out;
  }, [query, aircraft]);

  useEffect(() => setCursor(0), [query]);

  const pick = (icao24: string) => {
    select(icao24);
    setQuery('');
    setOpen(false);
    inputRef.current?.blur();
  };

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  // "/" focuses the search from anywhere on the map, like most map tools.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'SELECT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      e.preventDefault();
      inputRef.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="search-box" ref={rootRef}>
      <span className="search-box-icon" aria-hidden="true">
        <MagnifyingGlass size={15} weight="bold" />
      </span>
      <input
        ref={inputRef}
        type="text"
        value={query}
        placeholder="Find a flight"
        aria-label="Search flight callsign or hex code"
        autoComplete="off"
        spellCheck={false}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setCursor((c) => Math.min(c + 1, Math.max(matches.length - 1, 0)));
          }
          if (e.key === 'ArrowUp') {
            e.preventDefault();
            setCursor((c) => Math.max(c - 1, 0));
          }
          if (e.key === 'Enter' && matches[cursor]) pick(matches[cursor].icao24);
          if (e.key === 'Escape') {
            setOpen(false);
            inputRef.current?.blur();
          }
        }}
      />
      {!query && (
        <kbd className="search-box-kbd" aria-hidden="true">
          /
        </kbd>
      )}
      {open && query.trim() && (
        <ul className="header-popover search-box-results glass">
          {matches.length === 0 && (
            <li className="none">
              No aircraft in view match <span className="mono">{query.trim().toUpperCase()}</span>.
            </li>
          )}
          {matches.map((ac, i) => (
            <li key={ac.icao24}>
              <button
                className={i === cursor ? 'is-cursor' : ''}
                onMouseEnter={() => setCursor(i)}
                onClick={() => pick(ac.icao24)}
              >
                <span
                  className="alt-swatch"
                  style={{ background: altitudeColor(ac.altitudeFt, ac.onGround) }}
                  aria-hidden="true"
                />
                <span className="cs mono">{ac.callsign ?? ac.icao24.toUpperCase()}</span>
                <span className="hex mono">{ac.icao24.toUpperCase()}</span>
                <span className="alt mono">{formatAltitude(ac.altitudeFt, ac.onGround)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
